(() => {
  if (window.sfReviewUI) return;
  const palette = ['#E7DACF','#DCE6DA','#DAE3ED','#EADDE7','#E8E1C9','#D7E6E3','#EAD6D1','#D8DCEF','#E4DFD2','#D1E2DC','#E6D7E3','#DAE0CE'];
  const bodySelector = '.jdgm-rev__body, .jdgm-review-card__body, .jm-review-content__body';
  const nameSelector = '.jdgm-rev__author, .jdgm-review-card__name, .jm-reviewer-info__name, .jm-reviewer-name';
  const normalize = value => String(value || '').replace(/\s+/g,' ').trim();
  const hash = value => {
    let result = 2166136261;
    for (const char of value) result = Math.imul(result ^ char.charCodeAt(0),16777619);
    return result >>> 0;
  };
  const key = (name,text) => hash(normalize(`${name}\n${text}`)).toString(36);
  const color = identity => palette[hash(identity) % palette.length];
  const anonymous = name => /^(anonym|anonymous|anonyme[rn]?|a\.?|)$/i.test(normalize(name));
  const initials = name => {
    if (anonymous(name)) return 'A';
    const words = normalize(name).split(' ').filter(Boolean);
    return (Array.from(words[0])[0] + (words.length > 1 ? Array.from(words[words.length - 1])[0] : '')).toLocaleUpperCase('de-DE');
  };
  const metadata = (name,text,uuid) => {
    const entries = Object.values(window.jdgm?.data?.reviewWidget || {}).flatMap(widget => widget?.reviews || []);
    return entries.find(review => uuid && review.uuid === uuid) || entries.find(review => normalize(review.body) === normalize(text) && (normalize(review.reviewer_name) === normalize(name) || anonymous(name) && review.is_anonymous_reviewer));
  };
  const identity = (name,text,uuid,record) => {
    if (!anonymous(name)) return record?.gravatar_hash ? `person:${record.gravatar_hash}` : `name:${normalize(name).toLocaleLowerCase('de-DE')}`;
    return `anonymous:${record?.uuid || uuid || key(name,text)}`;
  };
  const avatar = (name,person,url) => {
    const node = document.createElement('span');
    node.className = 'sf-review-avatar';
    node.setAttribute('aria-hidden','true');
    node.dataset.sfPerson = person;
    node.style.setProperty('--sf-avatar-bg',color(person));
    node.textContent = initials(name);
    if (/^(https:\/\/|\/\/)/.test(url || '')) {
      const image = document.createElement('img');
      image.src = url;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 28;
      image.height = 28;
      image.addEventListener('error',() => { node.textContent = initials(name); },{once:true});
      node.replaceChildren(image);
    }
    return node;
  };
  const read = row => {
    const body = row.querySelector(bodySelector);
    if (!body) return null;
    const content = body.cloneNode(true);
    content.querySelectorAll('button,script,style').forEach(node => node.remove());
    const paragraphs = Array.from(content.querySelectorAll('p')).map(p => p.textContent.replace(/\s+/g,' ').trim()).filter(Boolean);
    const fullText = paragraphs.length ? paragraphs.join('\n\n') : content.textContent.replace(/\s+/g,' ').trim();
    if (!fullText) return null;
    const name = row.querySelector(nameSelector)?.textContent.trim() || 'Anonym';
    const uuid = row.getAttribute('review-uuid') || row.getAttribute('data-review-id') || '';
    const record = metadata(name,fullText,uuid);
    const explicitTitle = row.querySelector('.jm-review-content__title, .jdgm-review-card__title, .jdgm-rev__title')?.textContent.trim() || String(record?.title || '').trim();
    // Where a reviewer wrote a short opening paragraph rather than a formal title,
    // present that verbatim as the review headline instead of fabricating a headline.
    const firstParagraphIsTitle = !explicitTitle && paragraphs.length > 1 && paragraphs[0].length >= 10 && paragraphs[0].length <= 105;
    const title = explicitTitle || (firstParagraphIsTitle ? paragraphs[0] : '');
    const text = firstParagraphIsTitle ? paragraphs.slice(1).join('\n\n') : fullText;
    const scoreNode = row.querySelector('[data-score], [data-rating], [aria-label*="von 5"], [aria-label*="out of 5"], [aria-label*="star"]');
    const value = row.getAttribute('data-rating') || scoreNode?.getAttribute('data-score') || scoreNode?.getAttribute('data-rating') || scoreNode?.getAttribute('aria-label') || '';
    const match = value.match(/[0-5](?:[.,]\d+)?/);
    const score = match ? Math.min(5,Math.max(0,Number(match[0].replace(',','.')))) : record?.rating ?? null;
    const image = row.querySelector('.jdgm-rev__icon img, .jm-reviewer-avatar img, [class*="avatar"] img');
    const stamp = row.querySelector('time[datetime], .jm-reviewer-info__date, .jm-review-item__date, .jm-review-date, .jdgm-rev__timestamp, .jdgm-review-card__timestamp');
    const dateFormat = value => {
      const raw = String(value || '').trim();
      if (!raw) return '';
      const parts = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (parts) return parts[3]+'.'+parts[2]+'.'+parts[1];
      const parsed = new Date(raw);
      return Number.isNaN(parsed.getTime()) ? '' : new Intl.DateTimeFormat('de-DE',{day:'2-digit',month:'2-digit',year:'numeric'}).format(parsed);
    };
    const date = dateFormat(stamp?.getAttribute('datetime') || record?.created_at || record?.createdAt || record?.review_date || stamp?.textContent);
    // Never present the review date as the purchase date.
    const purchaseDate = dateFormat(record?.purchase_date || record?.purchased_at || record?.order_created_at || record?.order_date || record?.order?.created_at);
    return {name,title,text,score,date,purchaseDate,uuid:record?.uuid || uuid,identity:identity(name,text,uuid,record),avatar:image?.getAttribute('src') || record?.avatar_image_url || '',verified:Boolean(row.querySelector('.jdgm-rev__buyer-badge, .jm-verified-buyer-badge')) || row.getAttribute('data-verified-buyer') === 'true' || record?.verified_buyer === true};
  };
  const reader = data => {
    const node = document.createElement('div');
    node.className = 'sf-review-reader';
    node.append(avatar(data.name,data.identity,data.avatar));
    const name = document.createElement('span');
    name.textContent = data.name;
    node.append(name);
    return node;
  };
  const decorate = (row,data = read(row)) => {
    if (!data) return '';
    row.dataset.sfReviewKey = key(data.name,data.text);
    const host = row.querySelector('.jm-reviewer-avatar');
    if (host) {
      if (!host.dataset.sfPerson || host.dataset.sfPerson !== data.identity) {
        host.dataset.sfPerson = data.identity;
        host.style.setProperty('--sf-avatar-bg',color(data.identity));
      }
      const image = host.querySelector('img');
      if (!image) {
        host.classList.add('sf-native-initial-avatar');
        const initial = host.querySelector('.jm-reviewer-avatar__initial');
        if (initial && initial.textContent.trim() !== initials(data.name)) initial.textContent = initials(data.name);
      } else if (!image.dataset.sfFallbackReady) {
        image.dataset.sfFallbackReady = 'true';
        image.addEventListener('error',() => host.replaceChildren(avatar(data.name,data.identity)),{once:true});
      }
    } else {
      const info = row.querySelector('.jm-reviewer-info') || row.querySelector(nameSelector)?.parentElement;
      if (info && !info.querySelector('.sf-review-avatar')) info.prepend(avatar(data.name,data.identity,data.avatar));
    }
    return row.dataset.sfReviewKey;
  };
  const reviews = new Map();
  const remember = data => {
    const id = data.uuid || key(data.name,data.text);
    reviews.set(id,data);
    return id;
  };
  let opener = null;
  const close = () => document.querySelector('[data-sf-review-popup]')?.close();
  const open = (data,trigger) => {
    const dialog = document.querySelector('[data-sf-review-popup]');
    if (!dialog || !data || typeof dialog.showModal !== 'function') return;
    opener = trigger || document.activeElement;
    const person = dialog.querySelector('[data-sf-popup-reader]');
    person.replaceChildren(reader(data));
    const stars = dialog.querySelector('[data-sf-popup-stars]');
    stars.hidden = data.score === null;
    if (data.score !== null) {
      stars.style.setProperty('--sf-rating',`${data.score / 5 * 100}%`);
      stars.setAttribute('aria-label',`${data.score} / 5`);
    }
    const title = dialog.querySelector('[data-sf-popup-review-title]');
    title.textContent = data.title || '';
    title.hidden = !data.title;
    dialog.querySelector('[data-sf-popup-text]').textContent = data.text;
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('sf-modal-open');
  };
  document.addEventListener('click',event => {
    const trigger = event.target.closest('[data-sf-review-open]');
    if (!trigger) return;
    const data = reviews.get(trigger.dataset.sfReviewOpen);
    if (!data) return;
    event.preventDefault();
    open(data,trigger);
  });
  const mount = () => {
    const dialog = document.querySelector('[data-sf-review-popup]');
    if (!dialog || dialog.dataset.sfReady) return;
    dialog.dataset.sfReady = 'true';
    dialog.querySelector('[data-sf-popup-close]').addEventListener('click',close);
    dialog.addEventListener('click',event => { if (event.target === dialog) close(); });
    dialog.addEventListener('close',() => {
      if (!document.querySelector('dialog[open]')) document.body.classList.remove('sf-modal-open');
      if (opener?.isConnected && document.documentElement.dataset.sfInput === 'keyboard') opener.focus({preventScroll:true});
      else document.activeElement?.blur();
    });
    dialog.querySelector('[data-sf-popup-all]').addEventListener('click',event => {
      if (document.querySelector('[data-sf-inline-gallery]')) {
        event.preventDefault();
        close();
        document.querySelector('[data-sf-gallery-reviews-open]')?.click();
        return;
      }
      if (new URL(event.currentTarget.href).pathname === window.location.pathname) {
        event.preventDefault();
        close();
        document.querySelector('[data-sf-review-page]')?.scrollIntoView({block:'start',behavior:'instant'});
      }
    });
  };
  // Normalize independent Judge.me widget strings on async load.
  const normalizeReviewLabels = () => {
    document.querySelectorAll(
      '.sf-review-heading h2, .sf-gallery-reviews-heading h2, .sf-reviews-page .jdgm-rev-widg__title, .sf-reviews-page .jm-review-widget__title, .sf-judge-reviews .jdgm-rev-widg__title, .sf-judge-reviews .jm-review-widget__title, .sf-reviews-page h2'
    ).forEach(node => {
      const heading = node.textContent.trim();
      if (/^(Das sagen unsere Kunden|Das sagen die Kunden|Customer Reviews|Kundenbewertungen)(?:\s*\([0-9]+\))?$/i.test(heading)) {
        node.textContent = 'Das sagen die Leser';
      }
    });
    document.querySelectorAll(
      '.sf-reviews-page .jm-review-content__expand-button, .sf-reviews-page .jm-review-content__expand-button .jm-text, .sf-reviews-page .jdgm-rev__read-more, .sf-reviews-page .jdgm-review-card__read-more, .sf-reviews-page .sf-review-more-label, .sf-review-carousel .sf-review-read-more'
    ).forEach(node => {
      const label = node.querySelector?.('.jm-text, .sf-review-more-label') || node;
      if (/^((Ganze|Gesamte|Vollständige) (Bewertung(en)?|Rezension(en)?) (anzeigen|ansehen|lesen)|Mehr erfahren)$/i.test(label.textContent.trim())) {
        label.textContent = 'Mehr anzeigen';
      }
    });
  };
  let labelRaf = 0;
  const scheduleReviewLabels = () => {
    if (!labelRaf) labelRaf = requestAnimationFrame(() => {
      labelRaf = 0;
      normalizeReviewLabels();
    });
  };
  const mountReviewLabels = () => {
    normalizeReviewLabels();
    const main = document.querySelector('#MainContent');
    if (main && !main.dataset.sfReviewLabelObserver) {
      main.dataset.sfReviewLabelObserver = 'true';
      new MutationObserver(scheduleReviewLabels).observe(main,{childList:true,subtree:true,characterData:true});
    }
  };
  window.sfReviewUI = {key,color,identity,avatar,read,reader,decorate,remember,open};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',mountReviewLabels);
  else mountReviewLabels();
  document.addEventListener('shopify:section:load',scheduleReviewLabels);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',mount);
  else mount();
})();

